import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logEvent, LogLevel } from '@praxis/component-logging';
import {
  useNavigate,
  useSearchParams,
  useLocation,
  Link as ReactLink,
} from 'react-router-dom';
import { useInstallerAuthContext } from '../../hooks/useInstallerAuthContext';
import { useSurveyDispatch } from '../../hooks/useSurveyDispatch';
import {
  convertPhotosIntoFiles,
  doorImageSelector,
  doorTypeConditional,
  obstructionConditional,
} from '../../utils/surveyHelpers';
import { getMap, getMapExt } from '../../app/neptune/neptuneSlice';

import {
  Card,
  Grid,
  Layout,
  Breadcrumbs,
  Heading,
  Form,
  Input,
  Spinner,
  Anchor,
  ExpandableSection,
  Modal,
} from '@enterprise-ui/canvas-ui-react';
import { SurveyMap } from '../../components/SurveyMap';
import { Button, SelectInput } from 'ui-library';
import {
  InteriorOrExterior,
  LeftSideOrRightSide,
  SinlgeOrDoubleDoors,
  TMBlockers,
  anyObstruction,
} from './options';

import './styles/styles.scss';
import { PhotoUploader } from '../../components/PhotoUploader';
import { HeightFormField } from '../../components/HeightFormField';
import { convertFeetNumToDecimal } from '../../utils/helpers';

export const TeamMemberTemplate = () => {
  /* Auth related constants */
  const { user } = useInstallerAuthContext();
  const dispatch = useDispatch();
  /* Navigation related constants */
  const location = useLocation()?.state;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // const vestibule_number = searchParams.get('vestibule');
  let { vestibuleValue, vestibuleLabel, coordinates, totalReaders } =
    location ?? {};
  const floor_number = searchParams.get('floor');
  let prevData = JSON?.parse(sessionStorage?.getItem(vestibuleValue));
  let [formData, setFormData] = useState({
    ...location?.formData,
    ...prevData,
    vestibule_type: 'Type TM',
    floor_id: floor_number,
    entrance_door_count: 0.0,
    exit_door_count: 0.0,
    entrance_door_width: 0.0,
    exit_door_width: 0.0,
  });
  const [isPhotoMinHit, setIsPhotoMinHit] = useState(false);
  let [isPhotoMaxHit, setIsPhotoMaxHit] = useState(false);
  const [photoUploaded, setPhotoUploaded] = useState([]);
  let [submitErr, setSubmitErr] = useState(false);
  const [isUpdateValid, setIsUpdateValid] = useState(false);
  const isPhotoArrEmpty = Boolean(
    photoUploaded.length && photoUploaded.every(Boolean),
  );

  /* Location State related data*/
  const { dispatchEndpoint } = useSurveyDispatch(
    formData,
    photoUploaded,
    setIsPhotoMinHit,
  );
  const store = location?.formData?.location_id;
  const response = useSelector((state) => state.neptune);
  const { error, loading } = useSelector((state) => state.vestibuleForm);

  const canSubmit =
    [
      isUpdateValid,
      isPhotoArrEmpty,
      formData?.door_type,
      formData?.ceiling_height,
      formData?.is_obstructed_label,
      formData?.door_position,
    ].every(Boolean) &&
    doorTypeConditional(formData) &&
    obstructionConditional(formData);

  const handleSubmit = async () => {
    try {
      setSubmitErr(false);
      await convertPhotosIntoFiles(photoUploaded, formData);
      await dispatchEndpoint();
      navigate(`/survey-success`, {
        state: {
          formData,
          vestibuleValue,
          vestibuleLabel,
          totalReaders,
        },
      });
    } catch (err) {
      logEvent({ message: err }, { level: LogLevel.Error });
      setSubmitErr(true);
    }
  };
  useEffect(
    function dispatchMapRequest() {
      if (store) {
        let storeId = store.substr(1);
        let floorNum = floor_number;
        if (user) {
          let { token } = user;
          dispatch(getMapExt({ storeId, floorNum, token }));
        } else {
          dispatch(getMap({ storeId, floorNum }));
        }
      }
    },
    [dispatch, store, floor_number, user],
  );

  //timeout after 20 secs
  useEffect(() => {
    if (!loading) return;

    const timer = setTimeout(() => {
      setSubmitErr(true);
    }, 20000);

    return () => clearTimeout(timer);
  }, [loading]);

  return (
    <>
      <Layout.Body data-testid="vestibuleFormPage" includeRail>
        <Card className="hc-pa-normal">
          <Grid.Container>
            <Breadcrumbs className="breadcrumbSpacing">
              <Breadcrumbs.Item as={ReactLink} to="/">
                Home
              </Breadcrumbs.Item>
              <Breadcrumbs.Item as={ReactLink} to="/survey">
                Pre-Install Survey
              </Breadcrumbs.Item>
            </Breadcrumbs>
            <Grid.Item xs={12}>
              <Heading size={3}>
                {' '}
                <Heading size={3}>
                  {' '}
                  TM Entrance | Floor {floor_number} |{' '}
                  {formData?.device_type}{' '}
                </Heading>
              </Heading>
            </Grid.Item>
            {response?.error && (
              <Grid.Item xs={12}>
                <Card elevation={0}>
                  <Input.Info error>Map is currently unavailable.</Input.Info>
                </Card>
              </Grid.Item>
            )}
            {!response?.error && !response?.loading && coordinates && (
              <Grid.Item xs={12}>
                <SurveyMap
                  storeId={store}
                  floorNum={floor_number}
                  coordinates={coordinates}
                />
              </Grid.Item>
            )}
            {response?.loading && !response?.error && <Spinner />}
            {location?.formData && store ? (
              <Form
                role="form"
                id="registration_form"
                className="hc-mt-md"
                onSubmit={(e) => {
                  e.preventDefault();
                  sessionStorage.setItem(
                    vestibuleValue,
                    JSON?.stringify({ ...prevData, ...formData }),
                  );
                  handleSubmit();
                }}
              >
                <Grid.Container className="hc-pa-normal" align="center">
                  <Grid.Item xs={12}>
                    <SelectInput
                      id="door_position"
                      label="Is this an interior or exterior door?"
                      name="door_position"
                      classNamePrefix="customSelect"
                      options={InteriorOrExterior()}
                      required
                      defaultValue={{
                        value: formData?.door_position,
                        label: formData?.door_position,
                      }}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          door_position: e?.value,
                        });
                      }}
                    />
                    {formData?.door_position && (
                      <ExpandableSection className="hc-bg-grey06">
                        <p className="hc-pt-none">
                          Tap here to view example image
                        </p>
                        <ExpandableSection.Content className="hc-pa-none">
                          <img
                            width="100%"
                            height="100%"
                            src={doorImageSelector(formData?.door_position)}
                            alt="first_image"
                          />
                        </ExpandableSection.Content>
                      </ExpandableSection>
                    )}
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <SelectInput
                      data-testid="single_or_double_doors"
                      id="single_or_double_doors"
                      label="Is this a single-door or double-door setup?"
                      classNamePrefix="customSelect"
                      defaultValue={{
                        value: formData?.door_type,
                        label: formData?.door_type,
                      }}
                      required
                      options={SinlgeOrDoubleDoors()} //simplified options
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          door_type: e?.value,
                        });
                      }}
                    />
                  </Grid.Item>
                  {(formData?.door_type || prevData?.door_type) ===
                    'Double' && (
                    <Grid.Item xs={12}>
                      <SelectInput
                        data-testid="TM_use_both_doors"
                        id="TM_use_both_doors"
                        classNamePrefix="customSelect"
                        label="Do TMs use both doors for entry and/or exit?"
                        defaultValue={{
                          value: formData?.is_each_door_used,
                          label: formData?.is_each_door_used_label,
                        }}
                        required
                        options={anyObstruction()} //simplified options
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            is_each_door_used: Boolean(e?.value),
                            is_each_door_used_label: e?.label,
                          });
                        }}
                      />
                    </Grid.Item>
                  )}
                  {(formData?.door_type || prevData?.door_type) === 'Double' &&
                    (formData?.is_each_door_used_label ||
                      prevData?.is_each_door_used_label) === 'No' && (
                      <Grid.Item xs={12}>
                        <SelectInput
                          data-testid="left_or_right_side"
                          id="left_or_right_side"
                          label="From inside the store, facing the exterior, which door do TMs use?"
                          defaultValue={{
                            value: formData?.door_placement,
                            label: formData?.door_placement,
                          }}
                          classNamePrefix="customSelect"
                          required
                          options={LeftSideOrRightSide()} //simplified options
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              door_placement: e?.value,
                            });
                          }}
                        />
                      </Grid.Item>
                    )}
                  <Grid.Item xs={12}>
                    <HeightFormField
                      label="Height of the Ceiling:"
                      isChangeValid={setIsUpdateValid}
                      defaultValue={convertFeetNumToDecimal(
                        formData?.ceiling_height,
                      )}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          ceiling_height: e,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <SelectInput
                      data-testid="is_obstructed"
                      id="is_obstructed"
                      label="Are there any obstructions to the install"
                      classNamePrefix="customSelect"
                      defaultValue={{
                        value: formData?.is_obstructed,
                        label: formData?.is_obstructed_label,
                      }}
                      required
                      errorText="You must select an option"
                      options={anyObstruction()}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          is_obstructed: Boolean(e?.value),
                          is_obstructed_label: e?.label,
                        });
                      }}
                    />
                  </Grid.Item>
                  {(formData?.is_obstructed_label ||
                    prevData?.is_obstructed_label) === 'Yes' && (
                    <Grid.Item xs={12}>
                      <SelectInput
                        data-testid="obstruction_reason"
                        id="obstruction_reason"
                        label="Please select reason for obstruction:"
                        classNamePrefix="customSelect"
                        defaultValue={{
                          value: formData?.obstruction_reason,
                          label: formData?.obstruction_reason,
                        }}
                        required
                        options={TMBlockers()} //simplified options
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            obstruction_reason: e?.value,
                          });
                        }}
                      />
                    </Grid.Item>
                  )}
                  {(formData?.obstruction_reason ||
                    prevData?.obstruction_reason) === 'Other' &&
                    (formData?.is_obstructed_label ||
                      prevData?.is_obstructed_label) === 'Yes' && (
                      <Grid.Item xs={12}>
                        <Form.Field
                          data-testid="obstruction_other_reason"
                          id="obstruction_other_reason"
                          label="Please type reason for being unable to install:"
                          value={formData?.obstruction_other_reason}
                          required
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              obstruction_other_reason: e?.target?.value,
                            });
                          }}
                        />
                      </Grid.Item>
                    )}
                  <Grid.Item xs={12}>
                    <Form.Field
                      data-testid="other_details"
                      value={formData?.additional_notes}
                      id="more_details"
                      type="textarea"
                      label="Other details if applicable"
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          additional_notes: e?.target?.value || '',
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <PhotoUploader
                      files={photoUploaded}
                      onChange={setPhotoUploaded}
                      maximum={12}
                      minimum={2}
                      fileSizeLimit={5} //MB
                      isMaximumHit={isPhotoMaxHit}
                      isMinimumHit={isPhotoMinHit}
                      setMaxLimit={setIsPhotoMaxHit}
                      setMinLimit={setIsPhotoMinHit}
                    />
                  </Grid.Item>
                </Grid.Container>
                {loading && !submitErr && (
                  <Grid.Item xs={12} align="center" justify="center">
                    <Spinner />
                  </Grid.Item>
                )}
                <Grid.Item xs={12} align="center" justify="center">
                  <Button
                    className="hc-ma-normal"
                    label="Prev"
                    type="secondary"
                    name="prev_button"
                    id="prev_button"
                    onClick={() => {
                      sessionStorage.setItem(
                        vestibuleValue,
                        JSON?.stringify(formData),
                      );
                      navigate(`/survey-reader-list/?q=${store}`, {
                        state: { formData },
                      });
                    }}
                  />
                  <Button
                    className="hc-ma-normal"
                    label="Continue"
                    type="submit"
                    name="next_button"
                    id="submit"
                    disabled={!canSubmit}
                  />
                </Grid.Item>
                <Modal
                  isVisible={submitErr}
                  headingText={
                    error
                      ? 'Something went wrong...'
                      : 'This is taking longer than usual..'
                  }
                  onRefuse={() => setSubmitErr(false)}
                >
                  <Grid.Item xs={12} className="hc-pa-normal">
                    {error ? (
                      <>
                        <p className=" hc-mb-normal">
                          Failed to submit the survey form. Please try
                          resubmitting or or restarting your session.
                        </p>
                        <p className="hc-pb-expanded">
                          <em>{error}</em>
                        </p>
                      </>
                    ) : (
                      <>
                        <p className=" hc-mb-expanded">
                          This request is taking longer than expected and may
                          not complete -{' '}
                          <strong>large attachments can cause delays.</strong>
                        </p>
                        <p className=" hc-mb-expanded">
                          Please try again or upload smaller files.
                        </p>
                      </>
                    )}
                  </Grid.Item>
                </Modal>
              </Form>
            ) : (
              <Card elevation={0} className="hc-pa-normal cardContainer">
                <Input.Info error>
                  There was an issue loading the details for this location.
                  Please make sure{' '}
                  <Anchor to="/survey" as={ReactLink} className="iot-link">
                    previous information{' '}
                  </Anchor>{' '}
                  was submitted correctly.
                </Input.Info>
              </Card>
            )}
          </Grid.Container>
        </Card>
      </Layout.Body>
    </>
  );
};
